import * as React from 'react';
import styled, { keyframes } from 'styled-components';
import { ToastActionType, ToastColor, ToastEntryDirection, ToastPosition } from '../types';
import { BOXSHADOWS, COLORS} from '../styles';
import { Size } from '../types'
import { Button, IconButton } from '../Button';
import { ComponentSStyling, ComponentTextStyle } from '../styles';
import {HyperLinkDefaultStyling, HyperLinkInvertedStyling} from '../HyperLink';
import { SystemIcons } from '../icons';

const LoaderAnimationKeyframes = keyframes`
  0% {
    background-position: right;
  }
  100% {
    background-position: left;
  }
`;

const Loader = styled.div<{ duration: number }>`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 4px;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;

  animation: ${LoaderAnimationKeyframes} ${(props) => props.duration}ms cubic-bezier(0.18, 0.39, 0.35, 1);

  &.${ToastColor[ToastColor.BLACK]} {
    background: linear-gradient(to right, ${COLORS.neutral_200} 50%, ${COLORS.neutral_800} 50%) left;
    background-size: 200% 100%;
  }

  &.${ToastColor[ToastColor.WHITE]} {
    background: linear-gradient(to right, ${COLORS.neutral_400} 50%, ${COLORS.white} 50%) left;
    background-size: 200% 100%;
  }

  &.${ToastColor[ToastColor.BLUE]} {
    background: linear-gradient(to right, ${COLORS.accent1_200} 50%, ${COLORS.accent1_600} 50%) left;
    background-size: 200% 100%;
  }

  &.${ToastColor[ToastColor.GREEN]} {
    background: linear-gradient(to right, ${COLORS.correct_200} 50%, ${COLORS.correct_500} 50%) left;
    background-size: 200% 100%;
  }

  &.${ToastColor[ToastColor.ORANGE]} {
    background: linear-gradient(to right, ${COLORS.warning_200} 50%, ${COLORS.warning_500} 50%) left;
    background-size: 200% 100%;
  }

  &.${ToastColor[ToastColor.RED]} {
    background: linear-gradient(to right, ${COLORS.critical_200} 50%, ${COLORS.critical_500} 50%) left;
    background-size: 200% 100%;
  }
`;

export const ToastContainer = styled.div<{ animation: { duration: string; bezier: string; transform: string; keyframes: any }; withLoader: boolean; withClose: boolean }>`
  box-shadow: ${BOXSHADOWS.BOXSHADOW_L2};
  border-radius: 4px;

  animation: ${(props) => props.animation.keyframes} ${(props) => props.animation.duration} ${(props) => props.animation.bezier};
  transform: ${(props) => props.animation.transform};

  transition: all 300ms cubic-bezier(0.64, 0.04, 0.35, 1);

  padding: 0 ${(props) => (props.withClose ? '0' : '16px')} ${(props) => (props.withLoader ? '4px' : '0')} 16px;

  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 16px;
  justify-content: left;

  position: relative;

  width: max-content;
  min-height: 48px;
  box-sizing: border-box;

  ${ComponentSStyling(ComponentTextStyle.Regular, COLORS.white)}

  & a {
    display: inline;
    ${HyperLinkInvertedStyling}
  }

  &.${ToastColor[ToastColor.BLACK]} {
    background-color: ${COLORS.neutral_800};
  }

  &.${ToastColor[ToastColor.WHITE]} {
    background-color: ${COLORS.white};
    color: ${COLORS.black};

    & a {
      ${HyperLinkDefaultStyling}
    }
  }

  &.${ToastColor[ToastColor.BLUE]} {
    background-color: ${COLORS.accent1_600};
  }

  &.${ToastColor[ToastColor.GREEN]} {
    background-color: ${COLORS.correct_500};
  }

  &.${ToastColor[ToastColor.ORANGE]} {
    background-color: ${COLORS.warning_500};
  }

  &.${ToastColor[ToastColor.RED]} {
    background-color: ${COLORS.critical_500};
  }
`;

export const ToastActionButtons = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: center;

  & .action {
    height: unset;
  }
`;

export const ToastCloseButton = styled.div`
  & > button {
    margin-left: 16px;

    & svg {
      padding: 0;
    }
  }

  button svg path{
    fill: ${COLORS.white} !important;
  }

  &.${ToastColor[ToastColor.WHITE]} {
    button svg path {
      fill: ${COLORS.neutral_600} !important;
    }
  }
`;

export const ToastTextContainer = styled.span`
  font-feature-settings: 'liga' off;
  width: 240px;
  display: flex;
  padding: 16px 0;
`;

export const ToastIconContainer = styled.span`
  margin-right: -8px;
`;

export type ToastOptions = {
  color?: ToastColor;
  autoClose?: boolean;
  delay?: number;
  showCloseButton?: boolean;
  action?: {
    label: string;
    handler: () => void;
    type?: ToastActionType;
  }[];
  position: ToastPosition;
  enterFrom?: ToastEntryDirection;
  icon?: React.ReactNode;
};

type Props = {
  remove?: any;
  content: any;
  options?: ToastOptions;
  testId?: string;
};

const DEFAULT_OPTIONS: ToastOptions = {
  color: ToastColor.BLACK,
  autoClose: true,
  delay: 5000,
  showCloseButton: false,
  position: ToastPosition.TOPLEFT,
  enterFrom: ToastEntryDirection.TOP,
};

const Toast = ({ content, remove, options = DEFAULT_OPTIONS, testId }: Props) => {

  if(!remove)
    throw new Error('remove function is required when rendering Toast. Make sure that you are using useToast hook and ToastProvider for rendering Toasts');

  const removeRef = React.useRef();
  removeRef.current = remove;

  const elementRef = React.useRef<any>(null);

  const [shouldRemove, setShouldRemove] = React.useState(false);
  const [dimensions, setDimensions] = React.useState<{ width: number; height: number }>({ width: 0, height: 0 });

  const opts = { ...DEFAULT_OPTIONS, ...options };

  React.useEffect(() => {
    if (opts?.autoClose) {
      // @ts-ignore
      const id = setTimeout(() => setShouldRemove(true), opts.delay);
      return () => clearTimeout(id);
    }
    return undefined;
  }, []);

  React.useEffect(() => {
    if (shouldRemove) {
      // @ts-ignore
      const id = setTimeout(() => removeRef.current(), 200);
      return () => clearTimeout(id);
    }
  }, [shouldRemove]);

  React.useEffect(() => {
    setDimensions({ height: elementRef.current?.clientHeight, width: elementRef.current?.clientWidth });
  }, []);

  const animation = animations[shouldRemove ? 'leave' : 'enter'];
  const direction = animation[opts.enterFrom ?? ToastEntryDirection.TOP];

  const fromY = dimensions.height * direction.fromHeight;
  const toY = dimensions.height * direction.toHeight;
  const fromX = dimensions.width * direction.fromWidth;
  const toX = dimensions.width * direction.toWidth;

  let prop = {
    duration: animation.duration,
    bezier: animation.bezier,
    transform: `translateY(${toY}) translateX(${toX})`,
    keyframes: keyframes`
        0% {
          transform: translateY(${fromY}px) translateX(${fromX}px);
        }
        100% {
          transform: translateY(${toY}px) translateX(${toX}px);
        }`,
  };

  if (!dimensions.height) {
    prop = {
      ...prop,
      ...{
        transform: 'translateY(-1000vh) translateX(-1000vw)',
        keyframes: keyframes``,
      },
    };
  }

  const theme = ToastColor[opts.color ?? ToastColor.BLACK];

  if (!shouldRemove){
    setTimeout(() => elementRef?.current?.focus());
  }

  return (
    <ToastContainer data-testid={testId}
               animation={prop}
               ref={elementRef}
               withLoader={!!opts.autoClose}
               className={theme}
               withClose={!!opts.showCloseButton}
               tabIndex={-1}>
      {opts.icon && <ToastIconContainer>{opts.icon}</ToastIconContainer>}
      <ToastTextContainer className={'description'}>{content}</ToastTextContainer>
      {opts.action && (
        <ToastActionButtons>
          {opts.action.map((action, i) => (
            <Button
              size={Size.Small}
              variant={action.type === ToastActionType.SECONDARY ? 'secondary' : 'primary'}
              colorTheme={opts.color !== ToastColor.WHITE ? 'dark' : 'teal'}
              invertFocus={opts.color === ToastColor.BLACK}
              key={i}
              onClick={() => {
                setShouldRemove(true);
                action.handler();
              }}
              className={'action'}>
              {action.label}
            </Button>
          ))}
        </ToastActionButtons>
      )}
      {opts.showCloseButton && (
        <ToastCloseButton className={`close-button ${theme}`}>
          <IconButton variant={'secondary'}
                      shape="circular"
                      useTransparentBackground={true}
                      invertFocus={opts.color === ToastColor.BLACK}
                      action={(event) => setShouldRemove(true)}>
            <SystemIcons.Close size="24px" color={COLORS.neutral_600} />
          </IconButton>
        </ToastCloseButton>
      )}
      {!!opts.autoClose && <Loader className={`loader ${theme}`} duration={opts.delay ?? 0} />}
    </ToastContainer>
  );
};

export default Toast;

const animations = {
  enter: {
    bezier: 'cubic-bezier(0.22, 0.61, 0.35, 1)',
    duration: '300ms',
    [ToastEntryDirection.TOP]: {
      fromHeight: -1,
      toHeight: 0,
      fromWidth: 0,
      toWidth: 0,
    },
    [ToastEntryDirection.BOTTOM]: {
      fromHeight: 1,
      toHeight: 0,
      fromWidth: 0,
      toWidth: 0,
    },
    [ToastEntryDirection.LEFT]: {
      fromHeight: 0,
      toHeight: 0,
      fromWidth: -1,
      toWidth: 0,
    },
    [ToastEntryDirection.RIGHT]: {
      fromHeight: 0,
      toHeight: 0,
      fromWidth: 1,
      toWidth: 0,
    },
  },
  leave: {
    bezier: 'cubic-bezier(0.5, 0.05, 0.67, 0.19)',
    duration: '205ms',
    [ToastEntryDirection.TOP]: {
      fromHeight: 0,
      toHeight: -1,
      fromWidth: 0,
      toWidth: 0,
    },
    [ToastEntryDirection.BOTTOM]: {
      fromHeight: 0,
      toHeight: 1,
      fromWidth: 0,
      toWidth: 0,
    },
    [ToastEntryDirection.LEFT]: {
      fromHeight: 0,
      toHeight: 0,
      fromWidth: 0,
      toWidth: -1,
    },
    [ToastEntryDirection.RIGHT]: {
      fromHeight: 0,
      toHeight: 0,
      fromWidth: 0,
      toWidth: 1,
    },
  },
};
